import { PromotedActionsMenuEventCardProps } from '@eventbrite/ads-event-card-extra';
import { EVENTBRITE_CATEGORY_TO_REFINEMENT } from '@eventbrite/categories';
import { setWindowLocation } from '@eventbrite/http';
import { GenericLazyString } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React from 'react';
import {
    ARTICLE_TEMPLATE,
    CARD_BIG,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    FEATURED_TEMPLATE,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    LIST_CARD_TEMPLATE,
} from '../../constants';
import { FeatureFlags, ShareOptions } from '../../types/index';
import { BucketHeader } from '../BucketHeader';

type TrackClickFn = (identifer: { url?: string }) => void;

const ArticleCardGroup = loadable(() => import('./ArticleCardGroup'), {
    resolveComponent: (components: any) => components.ArticleCardGroup,
});
const FeaturedArticleCardGroup = loadable(() => import('./ArticleCardGroup'), {
    resolveComponent: (components: any) => components.FeaturedArticleCardGroup,
});
const BigEventCardGroup = loadable(() => import('./BigEventCardGroup'), {
    resolveComponent: (components: any) => components.BigEventCardGroup,
});
const SmallEventCardGroup = loadable(() => import('./SmallEventCardGroup'), {
    resolveComponent: (components: any) => components.SmallEventCardGroup,
});
const ListEventCardGroup = loadable(() => import('./ListEventCardGroup'), {
    resolveComponent: (components: any) => components.ListEventCardGroup,
});
const LoadingCards = loadable(() => import('../LoadingCards'), {
    resolveComponent: (components: any) => components.LoadingCards,
});

const CARD_GROUP_MAP = {
    [ARTICLE_TEMPLATE]: ArticleCardGroup,
    [FEATURED_TEMPLATE]: FeaturedArticleCardGroup,
    [LARGE_CARD_TEMPLATE_3]: BigEventCardGroup,
    [LARGE_CARD_TEMPLATE_6]: BigEventCardGroup,
    [LIST_CARD_TEMPLATE]: ListEventCardGroup,
    [CARD_TEMPLATE_4]: SmallEventCardGroup,
    [CARD_TEMPLATE_8]: SmallEventCardGroup,
    [CARD_BIG]: BigEventCardGroup,
};

type CardBucketProps = {
    /*
     * Array of preformatted objects that will represent that cards within the bucket
     */
    content: any[];
    /*
     * The manner in which this CardBucket should display its content
     */
    template: string;
    /*
     * The name of the given bucket ie. "Upcoming Music Events"
     */
    name: GenericLazyString;
    /*
     * The url clicking "See More" should connect the user to
     */
    linkUrl?: string;
    /*
     * Is user authenticated?
     */
    isAuthenticated?: boolean;
    /*
     * The key of the associated bucket
     */
    bucketKey?: string;
    /*
     * Tracking callback for event clicks
     */
    onTrackEventClickInternal?: Function;
    isLoading?: boolean;
    featureFlags: FeatureFlags;
    experiments?: any;
    shareOptions?: ShareOptions;
    affCode?: string;
    keyMin?: string;
    locale?: string;
    section?: string;
    seeMore?: {
        url?: string;
        params?: { dates?: string[]; places?: string[]; tags?: string[] };
    };
    locationSlug?: string;
    statsigLocationString?: string;
    promotedActionsData?: Pick<
        PromotedActionsMenuEventCardProps,
        'getTrackingContext' | 'onRemoveEvent'
    >;
};

export const CardBucket: React.FunctionComponent<CardBucketProps> = (props) => {
    const {
        content,
        name,
        template,
        linkUrl,
        isAuthenticated,
        experiments,
        featureFlags,
        bucketKey,
        isLoading,
        onTrackEventClickInternal,
        affCode,
        keyMin,
        locationSlug,
        seeMore,
        locale,
        statsigLocationString,
        promotedActionsData,
    } = props;

    let seeMoreLink = linkUrl;
    const DynamicCardGroup = (CARD_GROUP_MAP as any)[template];
    const enableClientSideLinking =
        keyMin === 'ccs' && featureFlags.indexCategoryBrowse;

    if (enableClientSideLinking && seeMore?.params?.tags?.[0]) {
        const categoryId = seeMore?.params?.tags?.[0] || '';
        const categoryRefinement =
            EVENTBRITE_CATEGORY_TO_REFINEMENT[categoryId];
        seeMoreLink = `/b/${locationSlug}/${categoryRefinement}/`;
    }

    const isSmallCity = template === CARD_BIG;

    const handleClick: TrackClickFn = ({ url }) => {
        if (url) {
            setWindowLocation(url);
        }
    };

    const handleTrackClickInternal = React.useCallback(
        (index, id, ticketsBy) => {
            const trackingData = {
                clickRank: index,
                navigation: `city_browse.${bucketKey}`,
                itemId: id,
            };

            onTrackEventClickInternal &&
                onTrackEventClickInternal(trackingData, ticketsBy);
        },
        [bucketKey, onTrackEventClickInternal],
    );

    return (
        <div>
            <BucketHeader
                title={name}
                linkUrl={seeMoreLink}
                template={template}
                bucketKey={bucketKey}
                enableClientSideLinking={enableClientSideLinking}
            />
            {isLoading ? (
                <section className="eds-l-pad-left-6">
                    <LoadingCards cardCount={3} />
                </section>
            ) : (
                <DynamicCardGroup
                    template={template}
                    cards={content}
                    linkUrl={seeMoreLink}
                    isAuthenticated={isAuthenticated}
                    handleClick={handleClick}
                    onTrackClickInternal={handleTrackClickInternal}
                    isSmallCity={isSmallCity}
                    title={name}
                    experiments={experiments}
                    featureFlags={featureFlags}
                    affCode={affCode}
                    enableClientSideLinking={enableClientSideLinking}
                    locale={locale}
                    statsigLocationString={statsigLocationString}
                    {...promotedActionsData}
                />
            )}
        </div>
    );
};

export default CardBucket;
